/* ============================================================
   storage — the trash.

   Deleting moves things here instead of destroying them (see
   "Trash" in server/nas.py). This is where they wait: what it
   was, where it was, who deleted it and when, and how long
   before it is cleared out for good — because the trash empties
   itself, after a number of days or sooner when a drive's trash
   is full, oldest first. Everything here can be put back where
   it came from, or deleted for good now.

   People see what they own or deleted; admins see everything.
   ============================================================ */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { Icon, fileIconName, fmtBytes, fmtDate } from './lib';
import { ConfirmModal } from './pickers';

function daysLeft(ts) {
  const d = Math.ceil((ts * 1000 - Date.now()) / 86400000);
  if (d <= 0) return 'today';
  return d === 1 ? '1 day' : `${d} days`;
}

export default function Trash({ token, me, drives, toast, onChanged, refreshKey, onOpenFolder }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sel, setSel] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [drive, setDrive] = useState('all');

  const load = useCallback(async (signal) => {
    try {
      const d = await api.trash(token, signal);
      setData(d); setError('');
      setSel((s) => new Set([...s].filter((id) => d.items.some((i) => i.id === id))));
    } catch (e) { if (e.name !== 'AbortError') setError(e.message); }
  }, [token]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load, refreshKey]);

  const label = (id) => drives.find((d) => d.id === id)?.label || id;
  const items = useMemo(() => (data?.items || []).filter((i) => drive === 'all' || i.drive === drive), [data, drive]);
  const chosen = items.filter((i) => sel.has(i.id));
  const allOn = items.length > 0 && chosen.length === items.length;
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const restore = async (list) => {
    setBusy(true);
    try {
      const r = await api.trashRestore(token, { ids: list.map((i) => i.id) });
      const back = r.restored || [];
      if (back.length === 1) {
        const b = back[0];
        toast?.('ok', `Restored ${b.name}`, `${label(b.drive)} ${b.path}${b.notes?.length ? ` — ${b.notes.join('; ')}` : ''}`,
          { label: 'Show', run: () => onOpenFolder?.(b.drive, b.path, b.kind === 'file' ? b.target_id : null) });
      } else if (back.length) {
        const noted = back.filter((b) => b.notes?.length);
        toast?.('ok', `Restored ${back.length} items`, noted.length ? noted.map((b) => `${b.name}: ${b.notes.join('; ')}`).join(' · ') : 'Each went back where it was.');
      }
      for (const f of r.failed || []) toast?.('err', `Could not restore ${f.name || 'an item'}`, f.error);
    } catch (e) {
      const f = e.body?.failed?.[0];
      toast?.('err', 'Could not restore', f ? `${f.name}: ${f.error}` : e.message);
    } finally {
      setBusy(false);
      load(); onChanged?.();
    }
  };

  const purge = (list) => setDialog({
    title: list.length === 1 ? `Delete ${list[0].name} for good` : `Delete ${list.length} items for good`,
    body: (
      <>
        <p>{list.length === 1 ? 'It' : 'They'} will be removed from the disk now, not in {daysLeft(list[0].purge_at)}.
          {list.some((i) => i.kind === 'folder') && ' Everything inside the folders goes too.'}</p>
        <p className="st-warn-line">This cannot be undone.</p>
      </>
    ),
    confirmLabel: 'Delete for good',
    onConfirm: async () => {
      const r = await api.trashPurge(token, list.map((i) => i.id));
      toast?.('ok', 'Deleted for good', `${r.purged} item${r.purged === 1 ? '' : 's'}`);
      for (const f of r.failed || []) toast?.('warn', 'Kept for now', f.error);
      load(); onChanged?.();
    },
  });

  const empty = () => setDialog({
    title: drive === 'all' ? 'Empty the trash' : `Empty the trash on ${label(drive)}`,
    body: (
      <>
        <p>
          {items.length} item{items.length === 1 ? '' : 's'} ({fmtBytes(items.reduce((n, i) => n + (i.size || 0), 0))})
          {me?.role === 'admin' ? ', everyone’s,' : ''} will be deleted for good.
        </p>
        <p className="st-warn-line">This cannot be undone.</p>
      </>
    ),
    confirmLabel: 'Empty trash',
    onConfirm: async () => {
      const r = await api.trashEmpty(token, drive === 'all' ? null : drive);
      toast?.('ok', 'Trash emptied', `${r.purged} item${r.purged === 1 ? '' : 's'} deleted for good`);
      setSel(new Set());
      load(); onChanged?.();
    },
  });

  const perDrive = data ? Object.entries(data.drives || {}).filter(([id]) => drives.some((d) => d.id === id)) : [];

  return (
    <section className="st-trash" aria-label="Trash">
      <div className="st-trash-head">
        <div className="st-trash-title">
          <h2 className="h2"><Icon name="trash" size={18}/> Trash</h2>
          {data && (
            <p className="st-trash-lead">
              Deleted things wait here for <b>{data.days} days</b>, then go for good — sooner if a drive’s trash
              fills up, oldest first. Nothing here counts against a drive’s allowance.
            </p>
          )}
        </div>
        <div className="st-trash-acts">
          <button type="button" className="btn btn--ghost btn--sm" disabled={!chosen.length || busy} onClick={() => restore(chosen)}>
            <Icon name="refresh" size={14}/> Restore{chosen.length ? ` ${chosen.length}` : ''}
          </button>
          <button type="button" className="btn btn--ghost btn--sm" disabled={!chosen.length || busy} onClick={() => purge(chosen)}>
            <Icon name="trash" size={14}/> Delete for good
          </button>
          <button type="button" className="btn btn--danger btn--sm" disabled={!items.length || busy} onClick={empty}>
            Empty trash
          </button>
        </div>
      </div>

      {perDrive.length > 0 && (
        <div className="st-trash-drives">
          <button type="button" className={`st-chip ${drive === 'all' ? 'on' : ''}`} aria-pressed={drive === 'all'}
                  onClick={() => setDrive('all')}>All drives</button>
          {perDrive.map(([id, t]) => (
            <button key={id} type="button" className={`st-chip st-trash-chip ${drive === id ? 'on' : ''}`} aria-pressed={drive === id}
                    onClick={() => setDrive(id)} title={`${fmtBytes(t.used)} of ${fmtBytes(t.cap)} this drive's trash holds`}>
              <span>{label(id)}</span>
              <span className="bar st-trash-bar"><span className={`fill ${t.used / t.cap > 0.9 ? 'fill--warn' : ''}`}
                    style={{ width: `${Math.min(100, (t.used / (t.cap || 1)) * 100)}%` }}/></span>
              <span className="st-trash-chipmeta tnum">{fmtBytes(t.used)} / {fmtBytes(t.cap)}</span>
            </button>
          ))}
        </div>
      )}

      {error && <div className="alert alert--err st-alert">{error}</div>}
      {!data && !error && (
        <div className="st-skeletons" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="skeleton st-skel-row"/>)}
        </div>
      )}
      {data && items.length === 0 && (
        <div className="empty st-trash-empty">
          <Icon name="trash" size={32}/>
          <p>The trash is empty.</p>
          <p className="meta">Deleted files and folders come here first.</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="st-trash-list" role="table" aria-label="Deleted items">
          <div className="st-trash-row st-trash-row--head" role="row">
            <span role="columnheader">
              <input type="checkbox" aria-label="Select all" checked={allOn}
                     onChange={() => setSel(allOn ? new Set() : new Set(items.map((i) => i.id)))}/>
            </span>
            <span role="columnheader">Name</span>
            <span role="columnheader">Was in</span>
            <span role="columnheader">Deleted</span>
            <span role="columnheader" className="st-trash-num">Size</span>
            <span role="columnheader" className="st-trash-num">Cleared in</span>
            <span role="columnheader"/>
          </div>
          {items.map((i) => {
            const where = i.kind === 'folder' ? (i.path.split('/').slice(0, -1).join('/') || '/') : i.path;
            return (
              <div key={i.id} role="row" className={`st-trash-row${sel.has(i.id) ? ' is-sel' : ''}`}
                   onClick={(e) => { if (e.target.closest('button, input')) return; toggle(i.id); }}>
                <span role="cell">
                  <input type="checkbox" aria-label={`Select ${i.name}`} checked={sel.has(i.id)} onChange={() => toggle(i.id)}/>
                </span>
                <span role="cell" className="st-trash-name" title={i.name}>
                  <Icon name={i.kind === 'folder' ? 'folder' : fileIconName(i.mime_type, i.name)} size={16}/>
                  <span className="st-trash-namet">{i.name}</span>
                  {i.kind === 'folder' && <span className="st-trash-sub">{i.files} file{i.files === 1 ? '' : 's'}</span>}
                </span>
                <span role="cell" className="st-trash-where" title={`${label(i.drive)} ${where}`}>
                  <span className="st-trash-drive">{label(i.drive)}</span> {where}
                </span>
                <span role="cell" className="st-trash-when">
                  {fmtDate(i.trashed_at)} · {i.trashed_by === me?.id ? 'you' : (i.trashed_by_name || '—')}
                  {i.owner_id !== i.trashed_by && i.owner_name && <span className="st-trash-sub"> (owner {i.owner_id === me?.id ? 'you' : i.owner_name})</span>}
                  <span className="st-trash-mob"> · {fmtBytes(i.size)} · cleared in {daysLeft(i.purge_at)}</span>
                </span>
                <span role="cell" className="st-trash-num tnum">{fmtBytes(i.size)}</span>
                <span role="cell" className="st-trash-num st-trash-left tnum" title={`Deleted for good on ${fmtDate(i.purge_at)} at the latest`}>
                  {daysLeft(i.purge_at)}
                </span>
                <span role="cell" className="st-trash-rowacts">
                  <button type="button" className="iconbtn" title="Restore" aria-label={`Restore ${i.name}`}
                          disabled={busy} onClick={() => restore([i])}><Icon name="refresh" size={15}/></button>
                  <button type="button" className="iconbtn" title="Delete for good" aria-label={`Delete ${i.name} for good`}
                          disabled={busy} onClick={() => purge([i])}><Icon name="trash" size={15}/></button>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {dialog && <ConfirmModal {...dialog} onClose={() => setDialog(null)}/>}
    </section>
  );
}
